import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Badge, Btn, eur } from "../components/common";
import { toast } from "sonner";
import { Plus, X, ExternalLink, RefreshCw } from "lucide-react";
import { WebhookLogPanel } from "../components/WebhookLogPanel";
import { ChangeEmailModal, PasswordActionsModal, TenantHistoryModal, LocalMetadataModal, TenantDetailsModal } from "./Privaxia";
import { CatalogPlansTab } from "../components/CatalogPlansTab";

const TABS = [
  { key: "tenants", label: "Tenants" },
  { key: "plans", label: "Planos" },
  { key: "billing", label: "Faturacao e add-ons" },
  { key: "webhook", label: "Webhook" },
  { key: "config", label: "Configuracao" },
];

export default function CogniEtica() {
  const [tab, setTab] = useState("tenants");
  const [status, setStatus] = useState(null);
  const [platformId, setPlatformId] = useState(null);

  useEffect(() => {
    api.get("/cognietica/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/platforms/lookup").then((r) => {
      const p = (r.data || []).find((x) => (x.slug || "").toLowerCase().includes("cognietica"));
      if (p) setPlatformId(p.id);
    }).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader overline="Portfolio - Denuncias" title="CogniEtica" />
      {status && !status.configured && (
        <div className="mb-6 px-4 py-3 rounded-lg bg-amber-50 text-amber-700 text-sm">
          Integracao nao configurada. Defina COGNIETICA_API_URL e COGNIETICA_PROVISIONING_KEY no backend.
        </div>
      )}
      <div className="flex gap-2 mb-8 border-b border-tint">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`ce-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-blue text-blue" : "border-transparent text-muted2 hover:text-navy"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "tenants" && <Tenants platformId={platformId} />}
      {tab === "plans" && <CatalogPlansTab platformId={platformId} platformSlug="cognietica" />}
      {tab === "billing" && <Billing />}
      {tab === "webhook" && <WebhookLogPanel origem="cognietica" title="Webhooks recebidos da CogniEtica" />}
      {tab === "config" && <Config status={status} />}
    </div>
  );
}

function Tenants({ platformId }) {
  const [rows, setRows] = useState([]);
  const [triageFilter, setTriageFilter] = useState("");   // "" | "cogniseg" | "client" | "none"
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [planFor, setPlanFor] = useState(null);
  const [emailFor, setEmailFor] = useState(null);   // {kind:'admin'|'triage', tenant, current}
  const [pwdFor, setPwdFor] = useState(null);       // {tenant, role, currentEmail}
  const [histFor, setHistFor] = useState(null);     // tenant
  const [detailsFor, setDetailsFor] = useState(null);   // tenant (ficha consolidada)
  const [metaFor, setMetaFor] = useState(null);         // tenant (ajustar metadata local)
  const [syncState, setSyncState] = useState(null);

  const loadSyncState = () => api.get("/cognietica/sync-status").then((r) => setSyncState(r.data)).catch(() => {});
  const load = () => api.get("/cognietica/tenants").then((r) => setRows(r.data.tenants || [])).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  const loadPlans = () => {
    if (!platformId) return;
    api.get(`/plans?plataforma_id=${platformId}`).then((r) => setPlans(r.data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    loadSyncState();
    loadPlans();
    // Espelha as subscricoes da CogniEtica para o Dashboard/gestao ficarem atualizados
    api.post("/cognietica/sync-subscriptions").then(loadSyncState).catch(() => {});
  }, [platformId]);

  const syncSubs = async () => {
    try {
      const { data } = await api.post("/cognietica/sync-subscriptions");
      toast.success(`${data.synced} subscricoes sincronizadas`);
      loadSyncState();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const act = async (slug, fn, label) => {
    try {
      await fn();
      toast.success(label);
      load();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (status === 404) {
        toast.error(detail, {
          action: { label: "Sincronizar agora", onClick: () => syncSubs() },
          duration: 8000,
        });
      } else {
        toast.error(detail);
      }
    }
  };

  const cols = [
    { key: "name", label: "Cliente", render: (r) => <span className="font-semibold text-navy">{r.name}</span> },
    { key: "slug", label: "Slug", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.slug}</code> },
    { key: "plan", label: "Plano", render: (r) => <Badge value={r.plan} /> },
    { key: "triagem", label: "Triagem", render: (r) => {
        if (r.triage_mode === "cogniseg") {
          return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-blue text-white" data-testid={`ce-triage-badge-${r.slug}`} title="Servico de triagem prestado pela Cogniseg (DPO)">
            <span>DPO</span><span className="opacity-80">Cogniseg</span>
          </span>;
        }
        if (r.triage_mode === "client") {
          return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`ce-triage-badge-${r.slug}`} title="Triagem gerida pelo proprio cliente">
            <span>Triagem</span><span className="opacity-80">Cliente</span>
          </span>;
        }
        return <span className="text-[10px] text-muted2 italic" title="Sem informacao - editar em Ficha -> Ajustar metadata local">n/d</span>;
      } },
    { key: "users_count", label: "Utiliz.", render: (r) => r.users_count ?? "-" },
    { key: "reports_count", label: "Denuncias", render: (r) => r.reports_count ?? "-" },
    {
      key: "public_report_url", label: "Canal publico",
      render: (r) => (
        <a href={r.public_report_url} target="_blank" rel="noreferrer" className="text-blue hover:text-light-blue inline-flex items-center gap-1 text-xs">
          {r.public_report_url} <ExternalLink size={12} />
        </a>
      ),
    },
    { key: "is_active", label: "Estado", render: (r) => <Badge value={r.is_active ? "ativa" : "suspensa"} /> },
    {
      key: "acoes", label: "Acoes",
      render: (r) => (
        <div className="flex gap-1 flex-wrap">
          {r.is_active ? (
            <button onClick={() => act(r.slug, () => api.delete(`/cognietica/tenants/${r.slug}`), "Tenant suspenso")} data-testid={`ce-suspend-${r.slug}`}
              className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100">Suspender</button>
          ) : (
            <button onClick={() => act(r.slug, () => api.post(`/cognietica/tenants/${r.slug}/reactivate`), "Tenant reativado")} data-testid={`ce-reactivate-${r.slug}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Reativar</button>
          )}
          <button onClick={() => setPlanFor(r)} data-testid={`ce-changeplan-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Mudar plano</button>
          <button onClick={() => setEmailFor({ kind: "admin", tenant: r, current: r.admin_email || "" })}
            data-testid={`ce-changeemail-admin-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Email admin</button>
          {(r.triage_mode === "client" || r.triage_email) && (
            <button onClick={() => setEmailFor({ kind: "triage", tenant: r, current: r.triage_email || "" })}
              data-testid={`ce-changeemail-triage-${r.slug}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Email triagem</button>
          )}
          <button onClick={() => setPwdFor({ tenant: r, role: "admin", currentEmail: r.admin_email || "" })}
            data-testid={`ce-password-admin-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Password admin</button>
          {r.triage_mode === "client" && (
            <button onClick={() => setPwdFor({ tenant: r, role: "triage_manager", currentEmail: r.triage_email || "" })}
              data-testid={`ce-password-triage-${r.slug}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Password triagem</button>
          )}
          <button onClick={() => setHistFor(r)}
            data-testid={`ce-history-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Historico</button>
          <button onClick={() => setDetailsFor(r)}
            data-testid={`ce-details-${r.slug}`}
            className="text-xs px-2 py-1 rounded bg-blue text-white hover:bg-navy font-semibold">Ficha</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="text-xs text-muted2" data-testid="ce-sync-state">
          {syncState?.last_run
            ? `Sincronizacao automatica diaria - ultima: ${new Date(syncState.last_run).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })} (${syncState.last_count ?? 0} subscricoes, ${syncState.trigger || "-"})`
            : "Sincronizacao automatica diaria ativa"}
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={syncSubs} testid="ce-sync-subs"><RefreshCw size={16} /> Sincronizar subscricoes</Btn>
          <Btn variant="ghost" onClick={async () => {
            try {
              const res = await api.get(`/upstream-docs/cognietica/download`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a"); a.href = url;
              a.download = "COGNIETICA_UPSTREAM_CONTRATOS.md";
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 5000);
              toast.success("Contratos upstream descarregados (para colar no projeto CogniEtica)");
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }} testid="ce-download-upstream-md">Contratos upstream</Btn>
          <Btn variant="ghost" onClick={load} testid="ce-refresh-tenants"><RefreshCw size={16} /> Atualizar</Btn>
          <Btn onClick={() => setOpen(true)} testid="ce-new-tenant"><Plus size={16} /> Novo tenant</Btn>
        </div>
      </div>

      {rows.length > 0 && (() => {
        const counts = { cogniseg: 0, client: 0, none: 0 };
        rows.forEach((r) => {
          if (r.triage_mode === "cogniseg") counts.cogniseg++;
          else if (r.triage_mode === "client") counts.client++;
          else counts.none++;
        });
        const chips = [
          { key: "", label: "Todos", n: rows.length, tone: "bg-navy text-white", inactive: "bg-white text-navy border-slate-200" },
          { key: "cogniseg", label: "DPO Cogniseg", n: counts.cogniseg, tone: "bg-blue text-white", inactive: "bg-white text-blue border-blue/40" },
          { key: "client", label: "Triagem Cliente", n: counts.client, tone: "bg-amber-500 text-white", inactive: "bg-white text-amber-700 border-amber-300" },
          { key: "none", label: "Sem info", n: counts.none, tone: "bg-slate-500 text-white", inactive: "bg-white text-slate-500 border-slate-300" },
        ];
        return (
          <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="ce-triage-filter-chips">
            {chips.map((c) => (
              <button key={c.key || "all"} onClick={() => setTriageFilter(c.key)}
                data-testid={`ce-triage-chip-${c.key || "all"}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${triageFilter === c.key ? c.tone + " border-transparent" : c.inactive + " hover:border-navy"}`}>
                {c.label} <span className="opacity-70">({c.n})</span>
              </button>
            ))}
          </div>
        );
      })()}
      <Table columns={cols} rows={rows.filter((r) => {
        if (!triageFilter) return true;
        if (triageFilter === "none") return !r.triage_mode;
        return r.triage_mode === triageFilter;
      })} testid="ce-tenants-table" />
      {open && <TenantModal plans={plans} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {planFor && <ChangePlanModal tenant={planFor} plans={plans} onClose={() => setPlanFor(null)} onSaved={() => { setPlanFor(null); load(); }} onSync={syncSubs} />}
      {emailFor && <ChangeEmailModal state={emailFor} platform="cognietica" onClose={() => setEmailFor(null)} onSaved={() => { setEmailFor(null); load(); }} onSync={syncSubs} />}
      {pwdFor && <PasswordActionsModal state={pwdFor} platform="cognietica" onClose={() => setPwdFor(null)} onSaved={() => setPwdFor(null)} onSync={syncSubs} />}
      {histFor && <TenantHistoryModal tenant={histFor} entidade="cognietica_tenant" onClose={() => setHistFor(null)} />}
      {detailsFor && (
        <TenantDetailsModal
          tenant={detailsFor}
          entidade="cognietica_tenant"
          platform="cognietica"
          onClose={() => setDetailsFor(null)}
          onAction={(kind) => {
            const t = detailsFor;
            setDetailsFor(null);
            if (kind === "change-email-admin") setEmailFor({ kind: "admin", tenant: t, current: t.admin_email || "" });
            else if (kind === "change-email-triage") setEmailFor({ kind: "triage", tenant: t, current: t.triage_email || "" });
            else if (kind === "password-admin") setPwdFor({ tenant: t, role: "admin", currentEmail: t.admin_email || "" });
            else if (kind === "password-triage") setPwdFor({ tenant: t, role: "triage_manager", currentEmail: t.triage_email || "" });
            else if (kind === "local-metadata") setMetaFor(t);
          }}
        />
      )}
      {metaFor && (
        <LocalMetadataModal tenant={metaFor} platform="cognietica"
          onClose={() => setMetaFor(null)}
          onSaved={() => { setMetaFor(null); load(); }} />
      )}
    </div>
  );
}

function TenantModal({ plans, onClose, onSaved }) {
  const firstPlan = plans[0] || {};
  const [form, setForm] = useState({
    name: "", slug: "",
    plan: firstPlan.upstream_plan_key || firstPlan.sku || "",
    plano_id: firstPlan.id || "",
    admin_email: "", nif: "", billing_email: "",
    periodo: (firstPlan.periodos_disponiveis || ["anual"])[0],
    triage_mode: "cogniseg", triage_name: "", triage_email: "", triage_password: "",
    account_id: "",
  });
  const [slugState, setSlugState] = useState(null);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/crm/accounts").then(({ data }) => setAccounts(data || [])).catch(() => {});
  }, []);

  const filteredAccounts = accounts.filter((a) =>
    !accountSearch || a.nome?.toLowerCase().includes(accountSearch.toLowerCase()) || String(a.nif || '').includes(accountSearch)
  ).slice(0, 12);

  const selectAccount = (acc) => {
    setForm((f) => ({ ...f, name: acc.nome || f.name, nif: acc.nif || f.nif, admin_email: acc.email || f.admin_email, billing_email: acc.contacto_faturacao_email || acc.email || f.billing_email, account_id: acc.id }));
    setAccountSearch(acc.nome || "");
    setShowAccountPicker(false);
  };

  const selectedPlan = plans.find((p) => p.id === form.plano_id) || {};
  const MENSAL_KEY = { anual: "preco_mensal_1ano", "3anos": "preco_mensal_3anos", "5anos": "preco_mensal_5anos" };
  const previewMrr = parseFloat(selectedPlan[MENSAL_KEY[form.periodo]] || 0);

  const selectPlan = (planId) => {
    const p = plans.find((x) => x.id === planId) || {};
    setForm((f) => ({ ...f, plano_id: planId, plan: p.upstream_plan_key || p.sku || "", periodo: (p.periodos_disponiveis || ["anual"])[0] }));
  };

  const triageOk = form.triage_mode === "cogniseg" ||
    (form.triage_mode === "client" && form.triage_name && form.triage_email && (form.triage_password || "").length >= 8);
  const canCreate = form.name && form.slug && form.plan && form.admin_email && triageOk;

  const checkSlug = async () => {
    if (!form.slug) return;
    try {
      const { data } = await api.get(`/cognietica/tenants/slug-check/${form.slug}`);
      setSlugState(data.available ?? data.is_available ?? null);
    } catch (e) {
      setSlugState(null);
    }
  };

  const save = async () => {
    if (!canCreate) return toast.error("Preencha todos os campos obrigatorios (incl. Gestor de Triagem)");
    const payload = { ...form };
    if (payload.triage_mode !== "client") {
      delete payload.triage_name;
      delete payload.triage_email;
      delete payload.triage_password;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/cognietica/tenants", payload);
      setCreated(data);
      if (data.upstream_ok) toast.success("Tenant criado e provisionado na plataforma");
      else toast.success("Tenant criado localmente (provisioning upstream pendente)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ce-tenant-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">Novo tenant CogniEtica</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        {!created ? (
          <>
            {/* Pesquisa de cliente existente */}
            <div className="mb-4 relative">
              <label className="block text-xs font-semibold text-navy mb-1">Selecionar cliente existente (opcional)</label>
              <div className="relative">
                <input value={accountSearch} onChange={(e) => { setAccountSearch(e.target.value); setShowAccountPicker(true); }}
                  onFocus={() => setShowAccountPicker(true)}
                  placeholder="Pesquisar por nome ou NIF..." data-testid="ce-account-search"
                  className="w-full px-3 py-2 border border-tint rounded-lg text-sm pr-8 focus:border-blue focus:outline-none" />
                {accountSearch && <button onClick={() => { setAccountSearch(""); setShowAccountPicker(false); upd("account_id", ""); }} className="absolute right-2 top-2.5 text-slate-400"><X size={14} /></button>}
              </div>
              {showAccountPicker && accountSearch && (
                <div className="absolute z-10 w-full bg-white border border-tint rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {filteredAccounts.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Nenhum cliente encontrado</div>}
                  {filteredAccounts.map((a) => (
                    <button key={a.id} onClick={() => selectAccount(a)} data-testid={`ce-account-option-${a.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-blue/5 text-sm">
                      <span className="font-semibold text-navy">{a.nome}</span>
                      {a.nif && <span className="ml-2 text-xs text-slate-400">NIF: {a.nif}</span>}
                      {a.email && <span className="ml-2 text-xs text-slate-400">{a.email}</span>}
                    </button>
                  ))}
                </div>
              )}
              {form.account_id && <div className="text-[10px] text-green-600 mt-0.5">Cliente CRM vinculado</div>}
            </div>
            <div className="border-t border-tint pt-3 mb-1"><span className="text-[10px] text-slate-400 uppercase tracking-wider">Dados do tenant</span></div>
            <label className="block text-sm font-semibold text-navy mb-1">Nome do cliente</label>
            <input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="ce-tenant-name" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
            <label className="block text-sm font-semibold text-navy mb-1">Slug (canal publico)</label>
            <div className="flex gap-2 mb-1">
              <input value={form.slug} onChange={(e) => upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} onBlur={checkSlug} data-testid="ce-tenant-slug" className="flex-1 px-3 py-2 border border-tint rounded-lg" />
              <Btn variant="ghost" onClick={checkSlug} testid="ce-slug-check">Verificar</Btn>
            </div>
            {slugState === true && <div className="text-xs text-green-600 mb-3">Slug disponivel - cognietica.pt/{form.slug}</div>}
            {slugState === false && <div className="text-xs text-red-600 mb-3">Slug ja em uso</div>}
            {slugState === null && <div className="mb-3" />}
            <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
            <select value={form.plano_id} onChange={(e) => selectPlan(e.target.value)} data-testid="ce-tenant-plan" className="w-full mb-2 px-3 py-2 border border-tint rounded-lg">
              {plans.map((p) => <option key={p.id} value={p.id}>{p.nome} ({p.sku})</option>)}
            </select>
            {selectedPlan.id && (
              <div className="mb-3 p-2.5 rounded-lg bg-tint text-xs space-y-1" data-testid="ce-plan-preview">
                <div className="flex justify-between"><span className="text-muted2">Max utilizadores</span><span className="font-semibold text-navy">{selectedPlan.max_utilizadores || "Ilimitado"}</span></div>
                {(selectedPlan.max_denuncias ?? -1) >= 0 && <div className="flex justify-between"><span className="text-muted2">Denuncias/mes</span><span className="font-semibold text-navy">{selectedPlan.max_denuncias}</span></div>}
                <div className="flex justify-between"><span className="text-muted2">MRR estimado</span><span className="font-bold text-blue">{previewMrr.toFixed(2)} EUR/mes</span></div>
              </div>
            )}
            <label className="block text-sm font-semibold text-navy mb-1">Email do admin</label>
            <input value={form.admin_email} onChange={(e) => upd("admin_email", e.target.value)} data-testid="ce-tenant-email" className="w-full mb-4 px-3 py-2 border border-tint rounded-lg" />

            <div className="border-t border-tint pt-4 mb-4">
              <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold mb-3">Dados para gestao da subscricao</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1">NIF</label>
                  <input value={form.nif} onChange={(e) => upd("nif", e.target.value)} data-testid="ce-tenant-nif" className="w-full px-3 py-2 border border-tint rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1">Periodo</label>
                  <select value={form.periodo} onChange={(e) => upd("periodo", e.target.value)} data-testid="ce-tenant-periodo" className="w-full px-3 py-2 border border-tint rounded-lg">
                    {(selectedPlan.periodos_disponiveis || ["anual"]).map((p) => (
                      <option key={p} value={p}>{p === "anual" ? "Anual (1 ano)" : p === "3anos" ? "3 anos" : p === "5anos" ? "5 anos" : p}</option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="block text-sm font-semibold text-navy mb-1 mt-3">Email de faturacao</label>
              <input value={form.billing_email} onChange={(e) => upd("billing_email", e.target.value)} data-testid="ce-tenant-billing-email" className="w-full px-3 py-2 border border-tint rounded-lg" placeholder="Se vazio, usa o email do admin" />
              <p className="text-xs text-muted2 mt-2">O plano define o MRR da subscricao (preco mensal). A subscricao fica ativa e visivel no Dashboard.</p>
            </div>

            <div className="border-t border-tint pt-4 mb-4">
              <div className="text-xs uppercase tracking-[0.15em] text-blue font-semibold mb-1">Gestor de Triagem (obrigatorio)</div>
              <p className="text-xs text-muted2 mb-3">Recebe todas as notificacoes do fluxo de denuncias.</p>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <button type="button" onClick={() => upd("triage_mode", "cogniseg")} data-testid="ce-triage-cogniseg"
                  className={`text-left border rounded-lg p-3 transition-colors ${form.triage_mode === "cogniseg" ? "border-blue bg-tint" : "border-tint hover:border-blue"}`}>
                  <div className="text-sm font-semibold text-navy">Servico CogniSeg</div>
                  <div className="text-xs text-muted2 mt-1">A CogniSeg gere a triagem.</div>
                </button>
                <button type="button" onClick={() => upd("triage_mode", "client")} data-testid="ce-triage-client"
                  className={`text-left border rounded-lg p-3 transition-colors ${form.triage_mode === "client" ? "border-blue bg-tint" : "border-tint hover:border-blue"}`}>
                  <div className="text-sm font-semibold text-navy">Do proprio cliente</div>
                  <div className="text-xs text-muted2 mt-1">Gestor interno do cliente.</div>
                </button>
              </div>
              {form.triage_mode === "client" && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1">Nome do gestor</label>
                    <input value={form.triage_name} onChange={(e) => upd("triage_name", e.target.value)} data-testid="ce-triage-name" className="w-full px-3 py-2 border border-tint rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1">Email do gestor</label>
                    <input value={form.triage_email} onChange={(e) => upd("triage_email", e.target.value)} data-testid="ce-triage-email" className="w-full px-3 py-2 border border-tint rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1">Password (min 8 caracteres)</label>
                    <input type="password" value={form.triage_password} onChange={(e) => upd("triage_password", e.target.value)} data-testid="ce-triage-password" className="w-full px-3 py-2 border border-tint rounded-lg" />
                    {form.triage_password && form.triage_password.length < 8 && <div className="text-xs text-red-600 mt-1">Minimo 8 caracteres.</div>}
                  </div>
                </div>
              )}
            </div>

            <Btn onClick={save} disabled={!canCreate || saving} testid="ce-tenant-save">{saving ? "A criar..." : "Criar tenant"}</Btn>
          </>
        ) : (
          <div>
            <div className="bg-green-50 text-green-700 rounded-lg p-4 text-sm mb-4">Tenant provisionado com sucesso.</div>
            <div className="space-y-2 text-sm mb-6">
              <div><span className="text-muted2">Canal publico:</span> <a className="text-blue" href={created.public_report_url || created.tenant?.public_report_url} target="_blank" rel="noreferrer">{created.public_report_url || created.tenant?.public_report_url}</a></div>
              {(created.admin_password || created.admin?.password) && (
                <div className="bg-tint rounded-lg p-3">
                  <div className="text-muted2 text-xs mb-1">Credenciais do admin (aparece so uma vez):</div>
                  <div><b>{created.admin_email || created.admin?.email || form.admin_email}</b></div>
                  <div>Password: <code>{created.admin_password || created.admin?.password}</code></div>
                </div>
              )}
              {created.triage_manager && (
                <div className="bg-tint rounded-lg p-3" data-testid="ce-triage-result">
                  <div className="text-muted2 text-xs mb-1">Gestor de Triagem ({created.triage_manager.mode === "client" ? "cliente" : "Servico CogniSeg"}):</div>
                  <div><b>{created.triage_manager.name}</b></div>
                  <div className="text-xs">{created.triage_manager.email}</div>
                </div>
              )}
            </div>
            <Btn onClick={onSaved} testid="ce-tenant-done">Concluir</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function ChangePlanModal({ tenant, plans, onClose, onSaved, onSync }) {
  const currentPlan = plans.find((p) => (p.upstream_plan_key || p.sku) === tenant.plan || p.nome === tenant.plan);
  const [planoId, setPlanoId] = useState(currentPlan?.id || plans[0]?.id || "");
  const [applyLimits, setApplyLimits] = useState(true);
  const [applyOverage, setApplyOverage] = useState(false);

  const selected = plans.find((p) => p.id === planoId) || {};
  const MENSAL_KEY = { anual: "preco_mensal_1ano", "3anos": "preco_mensal_3anos", "5anos": "preco_mensal_5anos" };

  const save = async () => {
    const plan_key = selected.upstream_plan_key || selected.sku || "";
    try {
      const { data } = await api.post(`/cognietica/tenants/${tenant.slug}/change-plan`, { plan: plan_key, plano_id: planoId, apply_limits: applyLimits, apply_overage_mode: applyOverage });
      if (data.upstream_ok) toast.success("Plano alterado e sincronizado");
      else toast.success("Plano alterado localmente (upstream pendente)");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ce-changeplan-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">Mudar plano - {tenant.name}</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Plano</label>
        <select value={planoId} onChange={(e) => setPlanoId(e.target.value)} data-testid="ce-changeplan-select" className="w-full mb-2 px-3 py-2 border border-tint rounded-lg">
          {plans.map((p) => <option key={p.id} value={p.id}>{p.nome} ({p.sku})</option>)}
        </select>
        {selected.id && (
          <div className="mb-4 p-2.5 rounded-lg bg-tint text-xs space-y-1">
            <div className="flex justify-between"><span className="text-muted2">Max utilizadores</span><span className="font-semibold text-navy">{selected.max_utilizadores || "Ilimitado"}</span></div>
            {(selected.max_denuncias ?? -1) >= 0 && <div className="flex justify-between"><span className="text-muted2">Denuncias/mes</span><span className="font-semibold text-navy">{selected.max_denuncias}</span></div>}
            {(selected.periodos_disponiveis || []).map((per) => (
              <div key={per} className="flex justify-between"><span className="text-muted2">MRR {per === "anual" ? "1 ano" : per}</span><span className="font-semibold text-navy">{(selected[MENSAL_KEY[per]] || 0).toFixed(2)} EUR</span></div>
            ))}
          </div>
        )}
        <label className="flex items-center gap-2 mb-2 text-sm text-navy"><input type="checkbox" checked={applyLimits} onChange={(e) => setApplyLimits(e.target.checked)} /> Aplicar limites do novo plano</label>
        <label className="flex items-center gap-2 mb-6 text-sm text-navy"><input type="checkbox" checked={applyOverage} onChange={(e) => setApplyOverage(e.target.checked)} /> Aplicar modo de excedente do novo plano</label>
        <Btn onClick={save} testid="ce-changeplan-save">Aplicar</Btn>
      </div>
    </div>
  );
}

function Plans() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/cognietica/plans").then((r) => setRows(r.data.plans || [])).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const cols = [
    { key: "name", label: "Plano", render: (r) => <span className="font-semibold text-navy">{r.name}</span> },
    { key: "key", label: "Chave", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.key}</code> },
    { key: "max_users", label: "Max utiliz." },
    { key: "max_reports_month", label: "Max denuncias/mes", render: (r) => (r.max_reports_month === -1 ? "ilimitado" : r.max_reports_month) },
    { key: "price_monthly", label: "Preco/mes", render: (r) => eur(r.price_monthly) },
    { key: "default_overage_mode", label: "Modo excedente", render: (r) => <Badge value={r.default_overage_mode || "-"} /> },
    { key: "extra_report_unit_price", label: "Preco extra/un.", render: (r) => eur(r.extra_report_unit_price) },
    { key: "acoes", label: "Acoes", render: (r) => <button onClick={() => setEdit(r)} data-testid={`ce-editplan-${r.key}`} className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white">Editar</button> },
  ];

  return (
    <div>
      <div className="flex justify-end mb-4 gap-2">
        <Btn variant="ghost" onClick={load}><RefreshCw size={16} /> Atualizar</Btn>
        <Btn onClick={() => setEdit({ _new: true, key: "", name: "", max_users: 5, max_reports_month: 50, price_monthly: 0, default_overage_mode: "billing_only", extra_report_unit_price: 0, description: "" })} testid="ce-new-plan"><Plus size={16} /> Novo plano</Btn>
      </div>
      <Table columns={cols} rows={rows} testid="ce-plans-table" />
      {edit && <PlanModal plan={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function PlanModal({ plan, onClose, onSaved }) {
  const isNew = plan._new;
  const [form, setForm] = useState({
    key: plan.key || "", name: plan.name || "", description: plan.description || "",
    max_users: plan.max_users ?? 5, max_reports_month: plan.max_reports_month ?? 50,
    price_monthly: plan.price_monthly ?? 0, default_overage_mode: plan.default_overage_mode || "billing_only",
    extra_report_unit_price: plan.extra_report_unit_price ?? 0,
  });
  const upd = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    const body = {
      ...form,
      max_users: Number(form.max_users), max_reports_month: Number(form.max_reports_month),
      price_monthly: Number(form.price_monthly), extra_report_unit_price: Number(form.extra_report_unit_price),
    };
    try {
      if (isNew) await api.post("/cognietica/plans", body);
      else await api.put(`/cognietica/plans/${plan.key}`, body);
      toast.success("Plano guardado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async () => {
    try {
      await api.delete(`/cognietica/plans/${plan.key}`);
      toast.success("Plano eliminado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ce-plan-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">{isNew ? "Novo plano" : `Editar ${form.name}`}</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Chave (key)</label>
        <input value={form.key} disabled={!isNew} onChange={(e) => upd("key", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} data-testid="ce-plan-key" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg disabled:bg-rowalt" />
        <label className="block text-sm font-semibold text-navy mb-1">Nome</label>
        <input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="ce-plan-name" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Descricao</label>
        <input value={form.description} onChange={(e) => upd("description", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Max utilizadores</label>
            <input type="number" value={form.max_users} onChange={(e) => upd("max_users", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Max denuncias/mes (-1=ilim.)</label>
            <input type="number" value={form.max_reports_month} onChange={(e) => upd("max_reports_month", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Preco/mes (EUR)</label>
            <input type="number" value={form.price_monthly} onChange={(e) => upd("price_monthly", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Preco extra/denuncia</label>
            <input type="number" value={form.extra_report_unit_price} onChange={(e) => upd("extra_report_unit_price", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
          </div>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Modo de excedente</label>
        <select value={form.default_overage_mode} onChange={(e) => upd("default_overage_mode", e.target.value)} data-testid="ce-plan-overage" className="w-full mb-6 px-3 py-2 border border-tint rounded-lg">
          <option value="billing_only">So faturacao</option>
          <option value="require_confirmation">Exigir confirmacao</option>
        </select>
        <div className="flex gap-2">
          <Btn onClick={save} testid="ce-plan-save">Guardar</Btn>
          {!isNew && <Btn variant="danger" onClick={del} testid="ce-plan-delete">Eliminar</Btn>}
        </div>
      </div>
    </div>
  );
}

function Billing() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tenants, setTenants] = useState([]);
  const [addonFor, setAddonFor] = useState(null);

  const load = () => api.get("/cognietica/tenants").then(async (r) => {
    const ts = r.data.tenants || [];
    const withBilling = await Promise.all(ts.map(async (t) => {
      try {
        const b = await api.get(`/cognietica/tenants/${t.slug}/billing?month=${month}`);
        return { ...t, billing: b.data };
      } catch (e) {
        return { ...t, billing: null };
      }
    }));
    setTenants(withBilling);
  }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  useEffect(() => { load(); }, [month]);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm font-semibold text-navy">Mes</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="ce-billing-month" className="px-3 py-2 border border-tint rounded-lg" />
        <Btn variant="ghost" onClick={load}><RefreshCw size={16} /> Atualizar</Btn>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tenants.map((t) => {
          const b = t.billing || {};
          const over = b.overage || 0;
          const blocked = b.overage_mode === "require_confirmation" && over > 0;
          return (
            <div key={t.slug} className="border border-tint rounded-lg p-6 bg-white" data-testid={`ce-billing-${t.slug}`}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-navy font-bold tracking-tight">{t.name}</h3>
                <Badge value={t.plan} />
              </div>
              <div className="text-sm space-y-1 text-body">
                <div className="flex justify-between"><span className="text-muted2">Denuncias</span><b>{b.reports_count ?? 0} / {b.effective_limit === -1 ? "ilimitado" : (b.effective_limit ?? b.base_limit ?? "-")}</b></div>
                <div className="flex justify-between"><span className="text-muted2">Limite base</span><span>{b.base_limit === -1 ? "ilimitado" : (b.base_limit ?? "-")}</span></div>
                <div className="flex justify-between"><span className="text-muted2">Add-on extra</span><span>{b.addon_extra ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-muted2">Excedente</span><b className={over > 0 ? "text-red-600" : ""}>{over}</b></div>
                <div className="flex justify-between"><span className="text-muted2">Modo</span><Badge value={b.overage_mode || "-"} /></div>
              </div>
              {blocked && (
                <div className="mt-4 bg-amber-50 text-amber-700 rounded-lg p-3 text-xs">
                  Gestao das denuncias em excesso bloqueada ate confirmar add-on.
                </div>
              )}
              {over > 0 && (
                <button onClick={() => setAddonFor({ tenant: t, billing: b, month })} data-testid={`ce-addon-${t.slug}`}
                  className="mt-4 w-full text-sm font-semibold text-white bg-blue hover:bg-light-blue rounded-lg py-2 transition-colors">
                  Comprar add-on ({over} denuncias)
                </button>
              )}
            </div>
          );
        })}
      </div>
      {addonFor && <AddonModal {...addonFor} onClose={() => setAddonFor(null)} onSaved={() => { setAddonFor(null); load(); }} />}
    </div>
  );
}

function AddonModal({ tenant, billing, month, onClose, onSaved }) {
  const [extra, setExtra] = useState(billing.overage || 1);
  const [unit, setUnit] = useState(0);
  const [note, setNote] = useState("");

  useEffect(() => {
    api.get("/cognietica/plans").then((r) => {
      const p = (r.data.plans || []).find((x) => x.key === tenant.plan);
      if (p && p.extra_report_unit_price) setUnit(p.extra_report_unit_price);
    }).catch(() => {});
  }, [tenant.plan]);

  const save = async () => {
    try {
      await api.post(`/cognietica/tenants/${tenant.slug}/addons`, {
        extra_reports: Number(extra), unit_price: Number(unit), note, month, confirmed: true,
      });
      toast.success("Add-on confirmado - limite aumentado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ce-addon-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">Add-on - {tenant.name}</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Denuncias extra</label>
        <input type="number" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="ce-addon-extra" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Preco unitario (EUR)</label>
        <input type="number" value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="ce-addon-unit" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Nota</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full mb-4 px-3 py-2 border border-tint rounded-lg" />
        <div className="bg-tint rounded-lg p-3 mb-6 flex justify-between text-sm">
          <span className="text-navy font-semibold">Total</span>
          <b className="text-navy">{eur(Number(extra) * Number(unit))}</b>
        </div>
        <Btn onClick={save} testid="ce-addon-save">Confirmar add-on</Btn>
      </div>
    </div>
  );
}

function Config({ status }) {
  const [config, setConfig] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [settings, setSettings] = useState(null);
  const [apiUrl, setApiUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [manualSecret, setManualSecret] = useState("");

  const loadSettings = () => api.get("/cognietica/settings").then((r) => {
    setSettings(r.data);
    setApiUrl(r.data.api_url || "");
  }).catch(() => {});

  useEffect(() => {
    loadSettings();
    api.get("/cognietica/config").then((r) => { setConfig(r.data); setBaseUrl(r.data.public_base_url || ""); }).catch(() => {});
    const backend = process.env.REACT_APP_BACKEND_URL;
    setWebhookUrl(`${backend}/api/webhooks/cognietica`);
  }, []);

  const saveCredentials = async () => {
    try {
      const body = { api_url: apiUrl };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      if (manualSecret.trim()) body.webhook_secret = manualSecret.trim();
      await api.put("/cognietica/settings", body);
      setApiKey("");
      setManualSecret("");
      toast.success("Credenciais guardadas (cifradas)");
      loadSettings();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const testConnection = async () => {
    try {
      const { data } = await api.post("/cognietica/settings/test");
      toast.success(`Ligacao OK - ${data.config?.platform_name || "CogniEtica"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveConfig = async () => {
    try {
      await api.put("/cognietica/config", { public_base_url: baseUrl });
      toast.success("Endereco publico sincronizado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const registerWebhook = async () => {
    try {
      const { data } = await api.put("/cognietica/webhook", { url: webhookUrl });
      setWebhookSecret(data.signing_secret || "");
      toast.success("Webhook registado - signing secret guardado cifrado");
      loadSettings();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div className="border border-tint rounded-lg p-6" data-testid="ce-credentials-card">
        <h3 className="text-navy font-bold text-lg tracking-tight mb-1">Ligacao a CogniEtica</h3>
        <p className="text-muted2 text-sm mb-4">
          Estas credenciais sao usadas apenas pelo backend do SaaS-Hub - a chave nunca vai ao browser e e guardada cifrada na base de dados.
        </p>
        <label className="block text-sm font-semibold text-navy mb-1">URL da API</label>
        <input value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} data-testid="ce-api-url" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" placeholder="https://cognietica.pt" />
        <label className="block text-sm font-semibold text-navy mb-1">Chave de API (X-API-Key)</label>
        <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} data-testid="ce-api-key" className="w-full mb-1 px-3 py-2 border border-tint rounded-lg font-mono" placeholder={settings?.api_key_set ? "Definida - deixe vazio para manter" : "Cole a nova chave"} />
        <div className="text-xs text-muted2 mb-4">
          {settings?.api_key_set
            ? `Atual: ${settings.api_key_masked} (origem: ${settings.api_key_source === "db" ? "definicoes" : "ambiente"})`
            : "Nenhuma chave definida."}
        </div>
        <div className="flex gap-2">
          <Btn onClick={saveCredentials} testid="ce-save-credentials">Guardar credenciais</Btn>
          <Btn variant="ghost" onClick={testConnection} testid="ce-test-connection"><RefreshCw size={16} /> Testar ligacao</Btn>
        </div>
        <p className="text-xs text-muted2 mt-3">
          Ao rodar a chave na CogniEtica: cole aqui a nova chave e guarde. Nao e preciso reiniciar nada.
        </p>
      </div>

      <div className="border border-tint rounded-lg p-6">
        <h3 className="text-navy font-bold text-lg tracking-tight mb-1">Endereco publico</h3>
        <p className="text-muted2 text-sm mb-4">O canal de cada cliente e {config?.public_url_pattern || "{public_base_url}/{slug}"}.</p>
        <label className="block text-sm font-semibold text-navy mb-1">public_base_url</label>
        <div className="flex gap-2">
          <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} data-testid="ce-config-baseurl" className="flex-1 px-3 py-2 border border-tint rounded-lg" placeholder="https://cognietica.pt" />
          <Btn onClick={saveConfig} testid="ce-config-save">Sincronizar</Btn>
        </div>
      </div>

      <div className="border border-tint rounded-lg p-6">
        <h3 className="text-navy font-bold text-lg tracking-tight mb-1">Webhook de estado (signing secret)</h3>
        <p className="text-muted2 text-sm mb-4">Recebe eventos de limites e alertas da CogniEtica, validados por HMAC. O signing secret e guardado cifrado.</p>
        <label className="block text-sm font-semibold text-navy mb-1">URL do recetor</label>
        <input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} data-testid="ce-webhook-url" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg text-sm" />
        <div className="text-xs text-muted2 mb-3">
          {settings?.webhook_secret_set ? `Signing secret guardado: ${settings.webhook_secret_masked}` : "Sem signing secret guardado."}
        </div>
        <Btn onClick={registerWebhook} testid="ce-webhook-register">Registar webhook e obter secret</Btn>
        {webhookSecret && (
          <div className="mt-3 bg-tint rounded-lg p-3 text-xs">
            <div className="text-muted2 mb-1">signing_secret (guardado cifrado no backend):</div>
            <code className="break-all">{webhookSecret}</code>
          </div>
        )}
        <div className="mt-5 pt-4 border-t border-tint">
          <label className="block text-sm font-semibold text-navy mb-1">Ou introduzir signing secret manualmente</label>
          <p className="text-xs text-muted2 mb-2">Se ja tem o secret gerado do lado da CogniEtica, cole-o aqui e guarde nas credenciais acima.</p>
          <input type="password" value={manualSecret} onChange={(e) => setManualSecret(e.target.value)} data-testid="ce-manual-secret" className="w-full px-3 py-2 border border-tint rounded-lg font-mono" placeholder="Cole o signing secret" />
          <div className="text-xs text-muted2 mt-1">Nota: sera guardado ao clicar em "Guardar credenciais".</div>
        </div>
      </div>
    </div>
  );
}
